import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Button, Radio, IconButton, Select, InputLabel, MenuItem } from '@material-ui/core'
import { Edit, Delete, Add, Close, ChevronLeft, ChevronRight, Group, PersonAdd, LocalOffer } from '@material-ui/icons'
import { DateTimePicker, MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'

import { TextField } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore from 'store'

import { 
    DialogHeader, 
    Header, 
    Body, 
    StyledClose,
    StyledIconButton,
    CategoryListTitleContainer,
    CategoryListTitleSubContainer1,
    CategoryListTitleSubContainer2,
    HeaderTitle1,
    HeaderTitle2,
    ImageHeader, 
    AddCategoryButton,
    CategoryList,
    CategoryListItem,
    CategoryListItemContainer,
    CategoryListItemActions,
    CategoryListItemContent,
    CategoryItemImageIcon,
    CategoryListItemTitle1,
    NewCatHeading3,
    NewCategoryBody,
    NewCategoryContainer,
    NewSubCategoryContainer,
    NewCatHeading4,
    NewCatHeading5,
    SearchContainer,
    NewCategoryContainerColumn,
    NewCategoryContainerActions,
    CategoryListItemIcon,
    CategoryListItemSubContent,
    CategoryListItemTitle2,
    CategoryListUserContent,
    CategoryListUserItem,
    CategoryListUserItem1,
    CategoryListUserItem2,
    ViewerModeContainer,
    ViewerModeOption,
    ViewerModeRow,
    ViewerModeIcon,
    ViewerModeTexts,
    ViewerModeTitle,
    ViewerModeSubtitle,
} from './CategoryModal.styles'

import { FormInput, FormTextarea, FormSelect, ColorPicker, FormSearch, FormTagSearch, UserOption } from './FormComponents'
import { parseUserTags } from './searchMatch'
import { Category } from './api'
import { getProtocolPermissions } from './permissions'
import { cleanImportedText } from './textUtils'
import RemoveCategoryModal from './RemoveCategoryModal'

import SettingsIcon from 'assets/icons/ims/Settings.svg'
import EditIcon from 'assets/icons/ims/Edit2.svg'
import DeleteIcon from 'assets/icons/ims/Delete.svg'
import Icon1 from 'assets/icons/protocols/icon1.svg'
import Icon2 from 'assets/icons/protocols/icon2.svg'
import Icon3 from 'assets/icons/protocols/icon3.svg'
import Icon9 from 'assets/icons/protocols/icon9.svg'
import Icon10 from 'assets/icons/protocols/icon10.svg'



const SaveButton = styled(Button)`
  display: flex;
  padding: 8.724px 23.631px 7.261px 23.992px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #155DFC;
  color: #FFF;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;  
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
`
const CancelButton = styled(Button)`
  display: flex;
  padding: 9.634px 23.98px 8.17px 24.901px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
`

// A failed save has to stay on screen: the snackbar auto-hides, and the form is
// still open with the values the server rejected.
const SaveError = styled.div`
  align-self: stretch;
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #FFC9C9;
  background: #FEF2F2;
  color: #C10007;
  text-align: right;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: pre-line;
`

// The protocols API has no field for viewer tags - CategoryRequest carries only
// Viewers - so a tag selection cannot be stored as such. It is saved by its
// meaning instead: the people currently carrying the chosen tags are written as
// the category's viewers, which the API does persist. On reopen the selection is
// recovered by matching those viewers back to whole tags.

type TagIndex = { [tag: string]: number[] }

// tag -> the users who carry it
const buildTagIndex = (users: UserOption[]): TagIndex => {
  const index: TagIndex = {}
  users.forEach(u => {
    parseUserTags((u as any).tags).forEach(tag => {
      const ids = index[tag] || (index[tag] = [])
      if (ids.indexOf(u.userId) === -1) ids.push(u.userId)
    })
  })
  return index
}

const usersForTags = (tags: string[], index: TagIndex): number[] => {
  const ids: number[] = []
  tags.forEach(tag => (index[tag] || []).forEach(id => {
    if (ids.indexOf(id) === -1) ids.push(id)
  }))
  return ids
}

// Which tags does this exact set of viewers represent? Only tags whose members
// are all viewers can be part of the answer, and they have to account for every
// viewer - otherwise the list was hand-picked and is shown as one. Among the
// candidates take the smallest covering set, largest tag first: plain
// containment would also return every tag that happens to be a subset of a
// bigger one, showing tags nobody selected.
const inferViewerTags = (viewerIds: number[], index: TagIndex): string[] => {
  if (!viewerIds.length) return []
  const isViewer = (id: number) => viewerIds.indexOf(id) !== -1

  const candidates = Object.keys(index)
    .filter(tag => index[tag].length > 0 && index[tag].every(isViewer))
    // deterministic: biggest tag first, then by name
    .sort((a, b) => index[b].length - index[a].length || a.localeCompare(b))

  const chosen: string[] = []
  const covered: number[] = []
  candidates.forEach(tag => {
    if (index[tag].some(id => covered.indexOf(id) === -1)) {
      chosen.push(tag)
      index[tag].forEach(id => { if (covered.indexOf(id) === -1) covered.push(id) })
    }
  })
  return covered.length === viewerIds.length ? chosen : []
}

const CategoryModal = ({ open, onCloseModal }: any) => {
  const { members, orgs, uiStore, memberStore, user, modulesStore, protocolsStore } = useStore()
  const { t } = useTranslation('protocols')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The required fields a save attempt was refused over - by this form or by
  // the api - so their borders show which ones to fix. Keyed the way the api
  // names them.
  const [invalidFields, setInvalidFields] = useState<string[]>([])
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryDescription, setCategoryDescription] = useState('')
  const [categoryIcon, setCategoryIcon] = useState('Users')
  const [categoryColor, setCategoryColor] = useState('#3B82F6')
  const categoryColors = ['#64748B', '#EC4899', '#06B6D4', '#EF4444', '#F59E0B', '#8B5CF6', '#10B981', '#3B82F6']
  const [categoryManagers, setCategoryManagers] = useState<UserOption[]>([])
  const [categoryApprovers, setCategoryApprovers] = useState<UserOption[]>([])
  const [categoryViewers, setCategoryViewers] = useState<UserOption[]>([])
  const tagIndex = buildTagIndex(protocolsStore.userList)
  type ViewerMode = 'all' | 'users' | 'tags'
  const [viewerMode, setViewerMode] = useState<ViewerMode>('all')
  const [categoryViewerTags, setCategoryViewerTags] = useState<string[]>([])

  const handleViewerModeChange = (mode: ViewerMode) => {
    setViewerMode(mode)
    if (mode !== 'users') setCategoryViewers([])
    if (mode !== 'tags') setCategoryViewerTags([])
  }
  const categoryIconOptions = [
    { value: 'Users',      label: 'Users' },
    { value: 'Building',   label: 'Building' },
    { value: 'Scale',      label: 'Scale' },
    { value: 'DollarSign', label: 'DollarSign' },
    { value: 'FileText',   label: 'FileText' },
    { value: 'Briefcase',  label: 'Briefcase' },
    { value: 'Home',       label: 'Home' },
    { value: 'Calendar',   label: 'Calendar' },
  ]
  
  const perms = getProtocolPermissions(protocolsStore.userPermissions)
  const categoryList = protocolsStore.categories

  useEffect(() => {
    if (!open) return

    if (protocolsStore.baseUrl == "") {
      protocolsStore.init(orgs.currentOrgId).then(data => {
        protocolsStore.loadCategories(orgs.currentOrgId)
        protocolsStore.loadUserList()
      })
    } else {
      protocolsStore.loadCategories(orgs.currentOrgId)
      protocolsStore.loadUserList()
    }
    
  }, [open])

  const resetForm = () => {
    setError(null)
    setInvalidFields([])
    setEditingCategory(null)
    setCategoryName('')
    setCategoryDescription('')
    setCategoryColor('#3B82F6')
    setCategoryIcon('Users')
    setCategoryManagers([])
    setCategoryApprovers([])
    setCategoryViewers([])
    setViewerMode('all')
    setCategoryViewerTags([])
  }

  const openEdit = (item: Category) => {
    setError(null)
    setInvalidFields([])
    setEditingCategory(item)
    setCategoryName(item.categoryName)
    setCategoryDescription(item.description || '')
    setCategoryColor(categoryColors[(item.colorId ?? 1) - 1] || '#3B82F6')
    setCategoryIcon(categoryIconOptions[(item.iconId ?? 1) - 1]?.value || 'Users')
    setCategoryManagers(item.categoryManagers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    setCategoryApprovers(item.approvingManagers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    setCategoryViewers(item.viewers.map(m => ({ userId: m.userId, fullName: m.fullName })))
    const inferredTags = item.viewerTags?.length
      ? item.viewerTags
      : inferViewerTags(item.viewers.map(m => m.userId), tagIndex)
    setCategoryViewerTags(inferredTags)
    setViewerMode(
      item.forAllCommunityUsers ? 'all'
      : inferredTags.length ? 'tags'
      : 'users'
    )
    setShowNewCategory(true)
  }

  const clearInvalid = (field: string) =>
    setInvalidFields(prev => (prev.includes(field) ? prev.filter(f => f !== field) : prev))

  const handleSave = async () => {
    // The api requires both, so the form names what is missing instead of
    // letting the save go out and come back rejected.
    const missing = [
      !categoryName.trim() ? { field: 'categoryname', label: t`category_name` } : null,
      !categoryDescription.trim() ? { field: 'description', label: t`description` } : null,
    ].filter(Boolean) as Array<{ field: string; label: string }>
    if (missing.length) {
      setInvalidFields(missing.map(m => m.field))
      setError(missing.map(m => t('app:field_required', { field: m.label })).join('\n'))
      return
    }
    setSaving(true)
    setError(null)
    setInvalidFields([])
    const colorId = categoryColors.indexOf(categoryColor) + 1
    const iconId = categoryIconOptions.findIndex(o => o.value === categoryIcon) + 1
    const body = {
      categoryName: categoryName.trim(),
      description: categoryDescription.trim(),
      colorId,
      iconId,
      forAllCommunityUsers: viewerMode === 'all',
      categoryManagers: categoryManagers.map(u => ({ userId: u.userId, memberRoleId: 1 })),
      approvingManagers: categoryApprovers.map(u => ({ userId: u.userId, memberRoleId: 2 })),
      viewers:
        viewerMode === 'users' ? categoryViewers.map(u => ({ userId: u.userId, memberRoleId: 3 }))
        : viewerMode === 'tags' ? usersForTags(categoryViewerTags, tagIndex).map(userId => ({ userId, memberRoleId: 3 }))
        : [],
      // Sent for the day the API stores it; ignored by the current one.
      viewerTags: viewerMode === 'tags' ? categoryViewerTags : [],
    }
    const res = editingCategory
      ? await protocolsStore.updateCategory(orgs.currentOrgId, editingCategory.id, body)
      : await protocolsStore.createCategory(orgs.currentOrgId, body)
    setSaving(false)
    // A rejected save answers with the reason on `error`; nothing at all means
    // the call never got through.
    const failure = !res ? t('app:server_problem') : (res as any).error
    if (failure) {
      setError(failure)
      // Mark whatever the api named, so the fields to fix are visible and not
      // only described.
      setInvalidFields(((res as any)?.errorFields || []) as string[])
      return
    }
    resetForm()
    setShowNewCategory(false)
  }

  const managerIds = new Set(categoryManagers.map(u => u.userId))
  const approverIds = new Set(categoryApprovers.map(u => u.userId))

  const managerOptions = protocolsStore.userList
  const approverOptions = protocolsStore.userList
  const viewerOptions = protocolsStore.userList.filter(u => !managerIds.has(u.userId) && !approverIds.has(u.userId))

  return (
    <>
        <RemoveCategoryModal
          open={!!deletingCategory}
          closeModal={() => setDeletingCategory(null)}
          categoryId={deletingCategory?.id}
          categoryName={cleanImportedText(deletingCategory?.categoryName)}
        />
        <Dialog
          style={{ zIndex: 1200 }} 
          open={open} 
          fullWidth 
          maxWidth={'lg'}
          scroll={'body'}
        >
          <DialogHeader>
            <Header>
                <CategoryListTitleContainer>
                    <CategoryListTitleSubContainer2>
                        <ImageHeader src={SettingsIcon} />
                    </CategoryListTitleSubContainer2>
                    <CategoryListTitleSubContainer1>
                        <HeaderTitle1>{t`categories_title`}</HeaderTitle1>
                        <HeaderTitle2>{t`categories_subtitle`}</HeaderTitle2>
                    </CategoryListTitleSubContainer1>
                </CategoryListTitleContainer>
                <StyledIconButton onClick={() => onCloseModal(false)}><Close style={{ color: '#364153' }} /></StyledIconButton>
            </Header>

          </DialogHeader>
          <DialogContent>
            {!showNewCategory && <Body>
                {perms.canCreateCategory && (
                  <AddCategoryButton onClick={() => { setError(null); setInvalidFields([]); setShowNewCategory(true) }}><Add/> {t`category_new`}</AddCategoryButton>
                )}
                <CategoryList>
                {protocolsStore.categories.map(item =>
                  <CategoryListItem key={item.id}>
                    <CategoryListItemContainer>
                        <CategoryListItemContent>
                          <CategoryListItemIcon bg={categoryColors[(item.colorId ?? 1) - 1]}>
                          </CategoryListItemIcon>
                          <CategoryListItemSubContent>
                            <CategoryListItemTitle1>{cleanImportedText(item.categoryName)}</CategoryListItemTitle1>
                            <CategoryListItemTitle2>{item.description}</CategoryListItemTitle2>
                            <CategoryListUserContent>
                              <CategoryListUserItem>
                                <CategoryListUserItem1><Image src={Icon9}/>{t`category_managers`}</CategoryListUserItem1>
                                <CategoryListUserItem2>{item.categoryManagers.length} {t`users_count`}</CategoryListUserItem2>
                              </CategoryListUserItem>
                              <CategoryListUserItem>
                                <CategoryListUserItem1><Image src={Icon10}/>{t`approvers`}</CategoryListUserItem1>
                                <CategoryListUserItem2>{item.approvingManagers.length} {t`users_count`}</CategoryListUserItem2>
                              </CategoryListUserItem>
                            </CategoryListUserContent>
                          </CategoryListItemSubContent>
                        </CategoryListItemContent>
                        <CategoryListItemActions>
                            {perms.canEditCategoryById(item.id) && (
                              <StyledIconButton onClick={() => openEdit(item)}><CategoryItemImageIcon src={EditIcon}/></StyledIconButton>
                            )}
                            {perms.canDeleteCategory && (
                              <StyledIconButton onClick={() => setDeletingCategory(item)}><CategoryItemImageIcon src={DeleteIcon}/></StyledIconButton>
                            )}
                        </CategoryListItemActions>
                    </CategoryListItemContainer>
                  </CategoryListItem>
                )}
                </CategoryList>
            </Body>}
            {showNewCategory && 
              <NewCategoryBody>
                <NewCatHeading3>{editingCategory ? t`category_edit` : t`category_new_heading`}</NewCatHeading3>
                <NewCategoryContainer>
                  <NewSubCategoryContainer style={{ height: 'auto', width: '100%' }}>
                    <FormInput
                      label={t`category_name`}
                      required={true}
                      invalid={invalidFields.includes('categoryname')}
                      value={categoryName}
                      onChange={value => { setCategoryName(value); clearInvalid('categoryname') }}
                      placeholder={t`category_name_placeholder`}
                    />
                  </NewSubCategoryContainer>
                </NewCategoryContainer>
                <NewCategoryContainer>
                  <NewSubCategoryContainer style={{ height: 'auto', width: '100%' }}>
                    <FormTextarea
                      label={t`description`}
                      required={true}
                      invalid={invalidFields.includes('description')}
                      value={categoryDescription}
                      onChange={value => { setCategoryDescription(value); clearInvalid('description') }}
                      placeholder={t`category_description_placeholder`}
                    />
                  </NewSubCategoryContainer>
                </NewCategoryContainer>
                <NewCategoryContainer>
                  <NewSubCategoryContainer>
                    <ColorPicker
                      label={t`color`}
                      value={categoryColor}
                      onChange={setCategoryColor}
                      colors={categoryColors}
                    />
                  </NewSubCategoryContainer>
                  <NewSubCategoryContainer>
                    <FormSelect
                      label={t`icon`}
                      required={false}
                      value={categoryIcon}
                      onChange={setCategoryIcon}
                      options={categoryIconOptions}
                    />
                  </NewSubCategoryContainer>
                </NewCategoryContainer>
                <NewCatHeading4>{t`access_permissions`}</NewCatHeading4>
                  <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon1}/>{t`category_managers_can`} </NewCatHeading5>
                    <SearchContainer>
                      <FormSearch
                        label={t`add_category_managers`}
                        value={categoryManagers}
                        onChange={setCategoryManagers}
                        options={managerOptions}
                        placeholder={t`search_by_name`}
                        tagColor={`#1447E6`}
                        tagBg={`#DBEAFE`}
                      />
                    </SearchContainer>
                  </NewCategoryContainerColumn>
                  <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon2}/>{t`approvers_can`} </NewCatHeading5>
                    <SearchContainer>
                      <FormSearch
                        label=""
                        value={categoryApprovers}
                        onChange={setCategoryApprovers}
                        options={approverOptions}
                        placeholder={t`search_user_to_add`}
                        tagColor={`#008236`}
                        tagBg={`#DCFCE7`}
                      />
                    </SearchContainer>
                  </NewCategoryContainerColumn>
                   <NewCategoryContainerColumn>
                    <NewCatHeading5><Image src={Icon3}/>{t`viewers_can`} </NewCatHeading5>
                    <ViewerModeContainer>
                      <ViewerModeOption selected={viewerMode === 'all'} onClick={() => handleViewerModeChange('all')}>
                        <ViewerModeRow>
                          <Radio checked={viewerMode === 'all'} color="primary" style={{ padding: 0 }} />
                          <ViewerModeIcon><Group style={{ fontSize: 20 }} /></ViewerModeIcon>
                          <ViewerModeTexts>
                            <ViewerModeTitle>{t`viewers_everyone`}</ViewerModeTitle>
                            <ViewerModeSubtitle>{t`viewers_everyone_sub`}</ViewerModeSubtitle>
                          </ViewerModeTexts>
                        </ViewerModeRow>
                      </ViewerModeOption>
                      <ViewerModeOption selected={viewerMode === 'users'} onClick={() => handleViewerModeChange('users')}>
                        <ViewerModeRow>
                          <Radio checked={viewerMode === 'users'} color="primary" style={{ padding: 0 }} />
                          <ViewerModeIcon><PersonAdd style={{ fontSize: 20 }} /></ViewerModeIcon>
                          <ViewerModeTexts>
                            <ViewerModeTitle>{t`viewers_specific`}</ViewerModeTitle>
                            <ViewerModeSubtitle>{t`viewers_specific_sub`}</ViewerModeSubtitle>
                          </ViewerModeTexts>
                        </ViewerModeRow>
                        {viewerMode === 'users' && (
                          <SearchContainer onClick={e => e.stopPropagation()}>
                            <FormSearch
                              label=""
                              value={categoryViewers}
                              onChange={setCategoryViewers}
                              options={viewerOptions}
                              placeholder={t`search_user_to_add`}
                              tagColor={`#59168B`}
                              tagBg={`#FAF5FF`}
                            />
                          </SearchContainer>
                        )}
                      </ViewerModeOption>
                      <ViewerModeOption selected={viewerMode === 'tags'} onClick={() => handleViewerModeChange('tags')}>
                        <ViewerModeRow>
                          <Radio checked={viewerMode === 'tags'} color="primary" style={{ padding: 0 }} />
                          <ViewerModeIcon><LocalOffer style={{ fontSize: 20 }} /></ViewerModeIcon>
                          <ViewerModeTexts>
                            <ViewerModeTitle>{t`viewers_by_tag`}</ViewerModeTitle>
                            <ViewerModeSubtitle>{t`viewers_by_tag_sub`}</ViewerModeSubtitle>
                          </ViewerModeTexts>
                        </ViewerModeRow>
                        {viewerMode === 'tags' && (
                          <SearchContainer onClick={e => e.stopPropagation()}>
                            <FormTagSearch
                              value={categoryViewerTags}
                              onChange={setCategoryViewerTags}
                              options={Object.keys(tagIndex)}
                              placeholder={t`search_tag_to_add`}
                              tagColor={`#59168B`}
                              tagBg={`#FAF5FF`}
                            />
                          </SearchContainer>
                        )}
                      </ViewerModeOption>
                    </ViewerModeContainer>
                  </NewCategoryContainerColumn>
                {error && <SaveError>{error}</SaveError>}
                <NewCategoryContainerActions>
                  <CancelButton onClick={() => { resetForm(); setShowNewCategory(false) }}>{t`cancel`}</CancelButton>
                  {/* Clickable even when something required is missing: pressing
                      it is how the form says which field that is, which a
                      disabled button cannot. */}
                  <SaveButton onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner size={16} /> : editingCategory ? t`save_category_changes` : t`category_create`}
                  </SaveButton>
                </NewCategoryContainerActions>
              </NewCategoryBody>}
            </DialogContent>
        </Dialog>
    </>
  )
}

export default observer(CategoryModal)


